import { useLingui } from '@lingui/react/macro';
import { useCallback } from 'react';

import {
  answerCall,
  CallRequestError,
  claimCall,
  fetchActiveCall,
  fetchCallChatPeer,
  hangupCall,
  startCall,
  type CallSessionPayload,
} from '#features/calls/model/calls';
import { useCallStore, type CallPeer } from '#features/calls/model/callStore';
import { toCallAvatarUrl } from '#features/calls/utils/callAvatar';
import { primeAudioPlayback } from '#features/calls/utils/primeAudioPlayback';
import { isCallOwnedElsewhere } from '#features/calls/utils/callOwnership';
import { dismissCallHere, isCallDismissedHere } from '#features/calls/utils/dismissedCalls';
import { toaster } from '#features/ui/components/Toaster';

/**
 * Имя и аватар собеседника, доехавшие раньше сервера: data-пуш о звонке несёт и то, и другое
 * (аватар — уже ресайзнутой ссылкой), а до этой правки всё уходило только в системное
 * уведомление. Дальше по ссылке из уведомления приложение знало один `callId` и добирало лицо
 * вторым запросом — отсюда «аватарка появилась не сразу».
 */
export type CallPeerHint = { callId: string; displayName: string | null; avatar: string | null };

/**
 * Server time the ring started, as local clock ms. A reload mid-ring must show the time
 * actually left, not a fresh countdown — but a malformed or absent instant must not turn
 * the countdown into NaN, so it degrades to "started now".
 */
const toRingingSince = (createdAt: string | null): number => {
  const parsed = createdAt ? Date.parse(createdAt) : Number.NaN;
  return Number.isNaN(parsed) ? Date.now() : parsed;
};

const toCallInfo = (
  session: CallSessionPayload,
  peer: CallPeer,
  startedByMe: boolean,
  acceptedAt: number | null
) => ({
  callId: session.id,
  chatId: session.chatId,
  roomName: session.roomName,
  peer: { ...peer, avatar: toCallAvatarUrl(peer.avatar) },
  startedByMe,
  withVideo: session.mediaType !== 'audio',
  livekitUrl: session.livekitUrl,
  token: session.token,
  acceptedAt,
  ringingSince: toRingingSince(session.createdAt),
});

/**
 * Профиль собеседника отдельным запросом: `call.active` отдаёт только его id. Раньше этот запрос
 * стоял ПЕРЕД показом звонка, и ответ на входящий физически ждал данные, нужные лишь для картинки.
 * На холодном старте приложения — а по нажатию в уведомлении он всегда холодный — это лишний круг
 * к серверу ровно в тот момент, когда человек уже нажал «Ответить». Теперь звонок показывается
 * сразу, а лицо дорисовывается, когда доедет.
 */
const patchPeerProfile = async (callId: string, chatId: string) => {
  let peerData: { displayName: string | null; avatar: string | null } | null = null;
  try {
    peerData = await fetchCallChatPeer(chatId);
  } catch {
    // Best-effort: на экране остаётся то, что уже показано.
    return;
  }
  if (!peerData) {
    return;
  }

  const call = useCallStore.getState().call;
  if (!call || call.callId !== callId) {
    // Звонок за это время кончился или сменился — дорисовывать нечего.
    return;
  }
  useCallStore.setState({
    call: {
      ...call,
      peer: {
        ...call.peer,
        displayName: peerData.displayName ?? call.peer.displayName,
        avatar: toCallAvatarUrl(peerData.avatar) ?? call.peer.avatar,
      },
    },
  });
};

const runResync = async (peerHint?: CallPeerHint) => {
  let session: CallSessionPayload | null = null;
  try {
    session = await fetchActiveCall();
  } catch {
    return;
  }

  const store = useCallStore.getState();

  if (!session) {
    if (store.phase === 'active' || store.phase === 'outgoing' || store.phase === 'incoming') {
      store.setEnded('ended');
    }
    return;
  }

  if (store.phase !== 'idle' && store.phase !== 'ended') {
    // Already tracking this call locally; refresh credentials if we lack them.
    if (store.call?.callId === session.id && !store.call.token && session.token) {
      useCallStore.setState((state) => ({
        call: state.call
          ? { ...state.call, token: session.token, livekitUrl: session.livekitUrl }
          : state.call,
      }));
    }
    return;
  }

  // Что успело доехать пушем — показываем сразу; остальное допишет patchPeerProfile.
  const hinted = peerHint && peerHint.callId === session.id ? peerHint : null;
  const peer: CallPeer = {
    userId: session.peerUserId,
    displayName: hinted?.displayName ?? null,
    avatar: hinted?.avatar ?? null,
  };
  // Пуш принёс и имя, и аватар — это тот же профиль, что отдал бы чат, и второй запрос за ним не
  // нужен. Но только если принёс ОБА: имени без лица достаточно, чтобы экран не выглядел пустым, и
  // недостаточно, чтобы отменить запрос — иначе аватар не появился бы уже никогда.
  const withProfile = () => {
    if (!hinted?.displayName || !hinted?.avatar) {
      void patchPeerProfile(session.id, session.chatId);
    }
  };

  if (session.status === 'ACCEPTED') {
    if (await isCallOwnedElsewhere(session.id)) {
      // Live in another tab of this browser — don't steal the room
      // (same identity would kick that tab out of the call).
      return;
    }
    if (!session.token) {
      // Токен есть только у владельца звонка. Пустой — значит разговор держит другое устройство
      // этого же человека: рвать его молча нельзя, поэтому показываем экран с переносом. Если
      // панель здесь уже закрыли руками — не поднимаем её заново.
      if (isCallDismissedHere(session.id)) {
        return;
      }
      store.setElsewhere(toCallInfo(session, peer, false, null));
      withProfile();
      return;
    }
    // Rejoin a live call after reload: restore and go straight to active.
    store.setOutgoing(toCallInfo(session, peer, false, Date.now()));
    store.setActive({ livekitUrl: session.livekitUrl, token: session.token });
    withProfile();
  } else if (session.status === 'RINGING') {
    // peerUserId is the OTHER participant: if the peer is the callee, I'm the caller.
    const startedByMe = session.peerUserId === session.calleeId;
    if (startedByMe) {
      store.setOutgoing(toCallInfo(session, peer, true, null));
    } else {
      store.setIncoming(toCallInfo(session, peer, false, null));
    }
    withProfile();
  }
};

/**
 * На холодном старте приложения сверку запрашивают сразу двое: нативный мост (ему надо понять,
 * что за звонок пришёл по ссылке из уведомления) и сам оверлей (он сверяется на каждом маунте).
 * Оба до первого await видят фазу `idle`, и на сервер уходило по два одинаковых круга запросов
 * в самый неудачный момент. Круг один на всех: второй вызов ждёт результат первого.
 */
let inFlightResync: Promise<void> | null = null;

/**
 * Дописать собеседника из подсказки, если сверку в этот момент вёл кто-то другой и подсказку тем
 * самым проглотил. Заполняем только пустые поля: данные чата свежее, чем то, что уехало в пуше.
 */
const applyPeerHint = (hint: CallPeerHint) => {
  const call = useCallStore.getState().call;
  if (!call || call.callId !== hint.callId || (call.peer.displayName && call.peer.avatar)) {
    return;
  }
  useCallStore.setState({
    call: {
      ...call,
      peer: {
        ...call.peer,
        displayName: call.peer.displayName ?? hint.displayName,
        avatar: call.peer.avatar ?? toCallAvatarUrl(hint.avatar),
      },
    },
  });
};

export const useCallActions = () => {
  const { t } = useLingui();

  const errorText = useCallback(
    (code: string): string => {
      switch (code) {
        case 'call.busy':
          return t`This player is busy in another call`;
        case 'call.already.active':
          return t`You already have an active call`;
        case 'call.rate.limited':
          return t`Too many calls. Please try again later`;
        case 'call.unavailable':
          return t`Calls are unavailable right now`;
        case 'call.not.ringing':
          return t`The call has already ended`;
        // Блок пары: шлюз отвечает `user.blocked.peer` (свой гейт), teamplay-comment на пути
        // звонка — тем же кодом; `user.not.permitted` оставлен для старых сборок сервисов.
        case 'user.blocked.peer':
        case 'user.not.permitted':
          return t`Calls are blocked between you and this player`;
        default:
          return t`Could not complete the call. Please try again`;
      }
    },
    [t]
  );

  const start = useCallback(
    async (peer: CallPeer, options?: { video?: boolean }) => {
      const store = useCallStore.getState();
      if (store.phase !== 'idle' && store.phase !== 'ended') {
        return;
      }

      // Show the call immediately, before the server has answered. Setting it up takes a
      // couple of seconds — presence lookup, a retry when the socket looks down — and during
      // that silence a tap on the call button looked like nothing had happened at all. Every
      // phone shows its call screen the instant you press dial; the connection catches up.
      store.setOutgoing({
        callId: '',
        chatId: '',
        roomName: '',
        peer: { ...peer, avatar: toCallAvatarUrl(peer.avatar) },
        startedByMe: true,
        withVideo: options?.video ?? false,
        livekitUrl: null,
        token: null,
        acceptedAt: null,
        // The server row does not exist yet; the countdown starts with the screen and is
        // re-based on `createdAt` a moment later, when start() answers.
        ringingSince: Date.now(),
      });

      try {
        const session = await startCall(peer.userId, options?.video ?? false);

        // Cancelled while we were connecting — don't resurrect the screen.
        if (useCallStore.getState().phase !== 'outgoing') {
          if (session.status !== 'MISSED') {
            hangupCall(session.id).catch(() => undefined);
          }
          return;
        }

        if (session.status === 'MISSED') {
          // Nothing to ring — no socket, no device. They get a missed-call notification.
          useCallStore.getState().setEnded('missed');
          toaster.create({
            type: 'info',
            duration: 5_000,
            title: t`This player is offline - we'll let them know you called`,
          });
          return;
        }

        useCallStore.getState().setOutgoing(toCallInfo(session, peer, true, null));
      } catch (error) {
        const code = error instanceof CallRequestError ? error.errorCode : 'global.error';
        useCallStore.getState().setEnded('ended');
        toaster.create({ type: 'error', duration: 5_000, title: errorText(code) });
      }
    },
    [errorText, t]
  );

  const answer = useCallback(async () => {
    const store = useCallStore.getState();
    const call = store.call;
    if (store.phase !== 'incoming' || !call) {
      return;
    }

    // Первым делом, ещё до сети: право играть звук выдаётся только внутри жеста, а комната
    // появится на секунду позже — там просить будет уже поздно.
    primeAudioPlayback();

    try {
      const session = await answerCall(call.callId);
      useCallStore.getState().setActive({
        livekitUrl: session.livekitUrl,
        token: session.token,
      });
    } catch (error) {
      const code = error instanceof CallRequestError ? error.errorCode : 'global.error';
      useCallStore.getState().setEnded('missed');
      toaster.create({ type: 'error', duration: 5_000, title: errorText(code) });
    }
  }, [errorText]);

  /**
   * Забрать разговор себе — то самое «слушать здесь» из плеера. Прежнее устройство узнаёт об этом
   * событием CALL_MOVED и отпускает комнату.
   */
  const claim = useCallback(async () => {
    const store = useCallStore.getState();
    const call = store.call;
    if (store.phase !== 'elsewhere' || !call) {
      return;
    }

    try {
      const session = await claimCall(call.callId);
      useCallStore.getState().setOutgoing({ ...call, acceptedAt: null });
      useCallStore.getState().setActive({
        livekitUrl: session.livekitUrl,
        token: session.token,
      });
    } catch (error) {
      const code = error instanceof CallRequestError ? error.errorCode : 'global.error';
      useCallStore.getState().setEnded('ended');
      toaster.create({ type: 'error', duration: 5_000, title: errorText(code) });
    }
  }, [errorText]);

  /** Decline an incoming ring, cancel an outgoing one, or end an active call. */
  const hangup = useCallback((reason: string = 'ended') => {
    const store = useCallStore.getState();
    const call = store.call;
    if (!call) {
      return;
    }

    // Snappy local end; the server CAS + WS fan-out settles both sides.
    store.setEnded(reason);
    if (call.callId) {
      hangupCall(call.callId).catch(() => undefined);
    }
    // No id yet means the call is still being created — start() sees the phase changed and
    // hangs it up as soon as the server hands one back.
  }, []);

  /**
   * Убрать панель «звонок открыт на другом устройстве» с этого клиента, не трогая разговор.
   * Красная трубка рядом с ней — это `hangup`, а он завершает звонок у обоих участников: не то,
   * что нужно человеку, который просто ушёл разговаривать за компьютер.
   */
  const dismissHere = useCallback(() => {
    const store = useCallStore.getState();
    if (store.phase !== 'elsewhere' || !store.call) {
      return;
    }
    dismissCallHere(store.call.callId);
    store.reset();
  }, []);

  /**
   * Reconcile local call state with the server — on mount (page reload during a
   * call) and after every WS reconnect (control events may have been missed).
   */
  const resync = useCallback(async (peerHint?: CallPeerHint): Promise<void> => {
    if (inFlightResync) {
      await inFlightResync;
    } else {
      // Никогда не отказывает: все, кто зовёт сверку, делают это через `void` и разбирать
      // исключение им нечем.
      const started = runResync(peerHint).catch(() => undefined);
      inFlightResync = started;
      // Окно склейки — текущий проход эффектов, а не всё время запроса. Мост и оверлей сверяются
      // в одном коммите, их и надо склеить; а вот сверка после обрыва WS обязана перечитать
      // сервер заново, а не получить ответ на запрос, отправленный до обрыва.
      queueMicrotask(() => {
        if (inFlightResync === started) {
          inFlightResync = null;
        }
      });
      await started;
    }

    if (peerHint) {
      applyPeerHint(peerHint);
    }
  }, []);

  return { start, answer, claim, hangup, resync, dismissHere };
};
